import {useEffect, useRef, useState} from 'react';

import {RANDOM_TRACK_ID, type Track} from '../../../shared/api/useMusic';
import {PlayIcon} from '../../../shared/components/icons';

const src = (file: string): string => (file.startsWith('/') ? file : `/audio/${file}`);

export const MusicPicker = ({
    tracks,
    value,
    disabled,
    onChange,
}: {
    tracks: Track[];
    value: string | null;
    disabled: boolean;
    onChange: (id: string | null) => void;
}) => {
    const audio = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const track = tracks.find((t) => t.id === value);

    useEffect(() => () => audio.current?.pause(), []);

    const toggle = () => {
        if (!track) return;
        if (playing) {
            audio.current?.pause();
            setPlaying(false);
            return;
        }
        const next = new Audio(src(track.file));
        next.addEventListener('ended', () => setPlaying(false));
        audio.current = next;
        setPlaying(true);
        next.play().catch(() => setPlaying(false));
    };

    return (
        <div className="bt-row">
            <select
                className="bt-field bt-field--grow"
                aria-label="Music"
                value={value ?? ''}
                disabled={disabled}
                onChange={(e) => {
                    audio.current?.pause();
                    setPlaying(false);
                    onChange(e.target.value || null);
                }}
            >
                <option value="">No music</option>
                {tracks.length > 1 && <option value={RANDOM_TRACK_ID}>Random</option>}
                {tracks.map((t) => (
                    <option key={t.id} value={t.id}>
                        {t.title}
                    </option>
                ))}
            </select>
            <button
                type="button"
                className="bt-button bt-button--ghost bt-button--small"
                aria-label={playing ? 'Stop preview' : 'Preview'}
                disabled={!track}
                onClick={toggle}
            >
                <PlayIcon />
            </button>
        </div>
    );
};
